import { useCallback, useRef } from 'react';

/**
 * Wraps an Apollo `refetch` (or any zero-arg function) in a **referentially
 * stable** callback whose identity never changes for the component's life,
 * while always invoking the latest underlying function.
 *
 * WHY: the group wrapper hooks expose `refetch` so callers can refresh on
 * demand. Returning a fresh `() => refetch()` arrow each render is a footgun —
 * any consumer that lists `refetch` in a `useEffect` dependency array (e.g.
 * MaterialsTab's "refresh on entry" effect) re-runs the effect on every render.
 * Combined with `fetchPolicy: 'cache-and-network'` + `notifyOnNetworkStatusChange`,
 * each refetch causes a re-render → a new arrow → the effect fires again →
 * an infinite request loop. Apollo 3.3.x does not guarantee a stable `refetch`
 * identity either, so we pin it through a ref.
 */
export const useStableRefetch = (refetch: () => unknown): (() => void) => {
  const ref = useRef(refetch);
  ref.current = refetch;
  return useCallback(() => {
    ref.current();
  }, []);
};
